import { useEffect, useState, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BlogManager } from "@/components/BlogManager";
import { AmbassadorPanel } from "@/components/AmbassadorPanel";
import { SocialConnectionsPanel } from "@/components/SocialConnectionsPanel";
import { DraggableSubTabs } from "@/components/DraggableSubTabs";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Globe, Share2, Copy, Link2, Facebook, Linkedin, Twitter, Send, MessageCircle,
  Mail, Plus, Trash2, Pencil, Check, X, Users, MousePointerClick, Download, Image as ImageIcon, Video, Megaphone, Handshake, Save, Loader2, Upload, CalendarClock, Clock, ImagePlus, List, CalendarDays, ChevronLeft, ChevronRight, Bookmark, Heart, Eye,
} from "lucide-react";

const enc = encodeURIComponent;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PARTNER_TYPES = [
  { value: "pompes_funebres", label: "Pompes funèbres" },
  { value: "fleuriste", label: "Fleuriste" },
  { value: "presse", label: "Presse / Média" },
  { value: "association", label: "Association" },
  { value: "influenceur", label: "Influenceur" },
  { value: "mairie", label: "Mairie / Collectivité" },
  { value: "autre", label: "Autre" },
];
const PARTNER_STATUS = [
  { value: "prospect", label: "Prospect", cls: "bg-white/10 text-muted-foreground" },
  { value: "contacte", label: "Contacté", cls: "bg-sky-500/15 text-sky-300" },
  { value: "actif", label: "Actif", cls: "bg-emerald-500/15 text-emerald-300" },
  { value: "inactif", label: "Inactif", cls: "bg-amber-400/15 text-amber-300" },
];
const typeLabel = (v) => PARTNER_TYPES.find((t) => t.value === v)?.label || "Autre";
const statusMeta = (v) => PARTNER_STATUS.find((s) => s.value === v) || PARTNER_STATUS[0];

const TEMPLATES = {
  fr: {
    general: "Allumez une bougie du souvenir en mémoire d'un être cher 🕯️\nPartagez un message et invitez vos proches à se recueillir. Gratuit, en quelques secondes.",
    candle: (n) => `En mémoire de ${n} 🕯️\nAllumez une bougie et laissez un message de soutien à la famille. C'est gratuit et cela ne prend qu'un instant.`,
  },
  en: {
    general: "Light a candle of remembrance for a loved one 🕯️\nShare a message and invite your family to gather. Free, in seconds.",
    candle: (n) => `In memory of ${n} 🕯️\nLight a candle and leave a message of support for the family. It's free and takes just a moment.`,
  },
  es: {
    general: "Enciende una vela del recuerdo en memoria de un ser querido 🕯️\nComparte un mensaje e invita a tus seres queridos a recogerse. Gratis, en segundos.",
    candle: (n) => `En memoria de ${n} 🕯️\nEnciende una vela y deja un mensaje de apoyo a la familia. Es gratis y solo toma un momento.`,
  },
};

const NETWORKS = [
  { key: "facebook", label: "Facebook", icon: Facebook, cls: "hover:border-[#1877F2]/60 hover:text-[#4293ff]" },
  { key: "x", label: "X", icon: Twitter, cls: "hover:border-white/40 hover:text-white" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, cls: "hover:border-[#0A66C2]/60 hover:text-[#4a9bff]" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, cls: "hover:border-[#25D366]/60 hover:text-[#3ee27a]" },
  { key: "telegram", label: "Telegram", icon: Send, cls: "hover:border-[#229ED9]/60 hover:text-[#4ab8ee]" },
  { key: "email", label: "E-mail", icon: Mail, cls: "hover:border-amber-300/60 hover:text-amber-200" },
];

const CAMPAIGN_NETS = [
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: ImageIcon },
  { key: "x", label: "X", icon: Twitter },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "telegram", label: "Telegram", icon: Send },
  { key: "tiktok", label: "TikTok", icon: Video },
  { key: "youtube", label: "YouTube", icon: Video },
  { key: "pinterest", label: "Pinterest", icon: ImageIcon },
];
const POST_STATUS = {
  brouillon: { label: "Brouillon", cls: "bg-white/10 text-muted-foreground" },
  programme: { label: "Programmé", cls: "bg-sky-500/15 text-sky-300" },
  publie: { label: "Publié", cls: "bg-emerald-500/15 text-emerald-300" },
};

const Section = ({ icon: Icon, title, desc, children, accent = "text-fuchsia-300" }) => (
  <div className="rounded-3xl border border-white/10 bg-card/40 p-5 sm:p-6">
    <div className="mb-1 flex items-center gap-2">
      <Icon size={17} className={accent} />
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
    </div>
    {desc && <p className="mb-4 text-sm text-muted-foreground">{desc}</p>}
    {children}
  </div>
);

const SocialPreview = ({ network, text, image }) => {
  const isIG = network === "instagram";
  const logo = "/logo192.png";
  const caption = text || "Votre texte apparaîtra ici…";
  return (
    <div className="w-full max-w-[320px] overflow-hidden rounded-xl border border-white/10 bg-white text-black shadow-lg" data-testid={`preview-${network}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <img src={logo} alt="" className="h-8 w-8 rounded-full border border-black/10 object-cover" />
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13px] font-semibold text-black">BougieDuCœur</div>
          <div className="text-[11px] text-neutral-500">{isIG ? "À l'instant" : "Publicité · 🌐"}</div>
        </div>
        <span className="ml-auto text-neutral-400">···</span>
      </div>
      {!isIG && <p className="whitespace-pre-wrap px-3 pb-2 text-[13px] leading-snug text-black">{caption}</p>}
      <div className="aspect-square w-full bg-neutral-100">
        {image ? (
          <img src={image} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-neutral-400">Ajoutez une image</div>
        )}
      </div>
      {isIG ? (
        <div className="px-3 py-2">
          <div className="mb-1 flex items-center gap-3 text-neutral-800">
            <Heart size={18} /><MessageCircle size={18} /><Send size={18} />
          </div>
          <p className="text-[13px] leading-snug text-black"><span className="font-semibold">bougieducoeur</span> <span className="whitespace-pre-wrap">{caption}</span></p>
        </div>
      ) : (
        <div className="flex items-center justify-between border-t border-black/10 px-3 py-2 text-[12px] font-medium text-neutral-600">
          <span className="inline-flex items-center gap-1"><span className="text-blue-600">👍</span> J'aime</span>
          <span>Commenter</span>
          <span>Partager</span>
        </div>
      )}
    </div>
  );
};

export function MarketingExterne({ candles = [] }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://bougieducoeur.com";
  const [sub, setSub] = useState("publications");

  // ---- Post generator ----
  const [lang, setLang] = useState("fr");
  const [target, setTarget] = useState("general");
  const paidCandles = useMemo(() => candles.filter((c) => c.paid && !c.is_expired), [candles]);
  const selectedCandle = candles.find((c) => c.id === target);
  const baseText = target === "general"
    ? TEMPLATES[lang].general
    : TEMPLATES[lang].candle(selectedCandle?.deceased_name || "");
  const [postText, setPostText] = useState(baseText);
  useEffect(() => { setPostText(baseText); }, [baseText]);

  const linkFor = (source) => {
    const path = target === "general" ? "/" : `/candle/${target}`;
    return `${origin}${path}?utm_source=${source}&utm_medium=social&utm_campaign=acquisition`;
  };
  const shareUrl = (net) => {
    const url = linkFor(net);
    const text = postText;
    switch (net) {
      case "facebook": return `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
      case "x": return `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
      case "linkedin": return `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
      case "whatsapp": return `https://wa.me/?text=${enc(text + "\n" + url)}`;
      case "telegram": return `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`;
      case "email": return `mailto:?subject=${enc("BougieDuCœur")}&body=${enc(text + "\n\n" + url)}`;
      default: return url;
    }
  };
  const copy = async (val, label = "Copié") => {
    try { await navigator.clipboard.writeText(val); toast.success(label); }
    catch { toast.error("Copie impossible"); }
  };

  // ---- Campagnes réseaux (programmation) ----
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const emptyPost = { networks: ["facebook"], text: "", image: null, mode: "schedule", scheduled_at: "", recurrence: "none" };
  const [pform, setPform] = useState(emptyPost);
  const [savingPost, setSavingPost] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);
  const postImgRef = useRef();

  const loadPosts = async () => {
    setLoadingPosts(true);
    try { const r = await api.get(`/admin/social-posts${accountId ? `?account_id=${accountId}` : ""}`); setPosts(r.data); } catch { setPosts([]); } finally { setLoadingPosts(false); }
  };
  useEffect(() => { loadPosts(); }, []);

  const toggleNet = (k) => setPform((f) => ({ ...f, networks: f.networks.includes(k) ? f.networks.filter((n) => n !== k) : [...f.networks, k] }));
  const onPostImage = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image trop lourde (max 4 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => setPform((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };
  const savePost = async () => {
    if (!pform.text.trim() && !pform.image) return toast.error("Ajoutez un texte ou une image");
    if (pform.networks.length === 0) return toast.error("Choisissez au moins un réseau");
    if (pform.mode === "schedule" && !pform.scheduled_at) return toast.error("Choisissez une date et une heure");
    setSavingPost(true);
    try {
      const payload = { account_id: accountId, networks: pform.networks, text: pform.text, image: pform.image };
      if (pform.mode === "schedule") { payload.scheduled_at = new Date(pform.scheduled_at).toISOString(); payload.status = "programme"; payload.recurrence = pform.recurrence || "none"; }
      else { payload.status = "brouillon"; }
      await api.post("/admin/social-posts", payload);
      toast.success(pform.mode === "schedule" ? "Publication programmée" : "Brouillon enregistré");
      setPform(emptyPost); setShowPostForm(false); loadPosts();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSavingPost(false); }
  };
  const markPublished = async (id) => { try { await api.patch(`/admin/social-posts/${id}`, { status: "publie" }); loadPosts(); toast.success("Marquée comme publiée"); } catch { toast.error("Erreur"); } };
  const deletePost = async (id) => { try { await api.delete(`/admin/social-posts/${id}`); setPosts((l) => l.filter((p) => p.id !== id)); toast.success("Supprimée"); } catch { toast.error("Erreur"); } };
  const campaignLink = (net) => `${origin}/?utm_source=${net}&utm_medium=social&utm_campaign=campagne`;
  const publishTo = (net, text) => {
    const url = campaignLink(net);
    let u = null;
    switch (net) {
      case "facebook": u = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`; break;
      case "x": u = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`; break;
      case "linkedin": u = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`; break;
      case "whatsapp": u = `https://wa.me/?text=${enc(text + "\n" + url)}`; break;
      case "telegram": u = `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`; break;
      default: u = null;
    }
    if (!u) { copy(text, "Légende copiée — collez-la sur Instagram"); window.open("https://www.instagram.com/", "_blank"); return; }
    window.open(u, "_blank");
  };
  const fmtDate = (d) => { try { return new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return d; } };

  // ---- Vue calendrier ----
  const [viewMode, setViewMode] = useState("list");
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const monthLabel = calMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const buildCalendar = () => {
    const year = calMonth.getFullYear(), month = calMonth.getMonth();
    const startDay = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };
  const postsOnDay = (date) => posts.filter((p) => { if (!p.scheduled_at) return false; const d = new Date(p.scheduled_at); return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate(); });

  // ---- Modèles de publications ----
  const [templates, setTemplates] = useState([]);
  const loadTemplates = async () => { try { const r = await api.get("/admin/post-templates"); setTemplates(r.data); } catch { setTemplates([]); } };
  useEffect(() => { loadTemplates(); }, []);
  const applyTemplate = (id) => { const t = templates.find((x) => x.id === id); if (!t) return; setPform((f) => ({ ...f, networks: t.networks?.length ? t.networks : f.networks, text: t.text || "", image: t.image || null })); toast.success(`Modèle « ${t.name} » chargé`); };
  const saveAsTemplate = async () => {
    const name = window.prompt("Nom du modèle ?"); if (!name) return;
    try { await api.post("/admin/post-templates", { name, networks: pform.networks, text: pform.text, image: pform.image }); toast.success("Modèle enregistré"); loadTemplates(); }
    catch { toast.error("Erreur"); }
  };
  const deleteTemplate = async (id) => { try { await api.delete(`/admin/post-templates/${id}`); setTemplates((l) => l.filter((t) => t.id !== id)); toast.success("Modèle supprimé"); } catch { toast.error("Erreur"); } };

  // ---- Partners CRM ----
  const [partners, setPartners] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const emptyForm = { name: "", type: "pompes_funebres", contact_name: "", email: "", phone: "", website: "", status: "prospect", notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPartners = async () => {
    setLoadingP(true);
    try { const r = await api.get("/admin/partners"); setPartners(r.data); }
    catch { setPartners([]); } finally { setLoadingP(false); }
  };
  useEffect(() => { loadPartners(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({ name: p.name || "", type: p.type || "autre", contact_name: p.contact_name || "", email: p.email || "", phone: p.phone || "", website: p.website || "", status: p.status || "prospect", notes: p.notes || "" });
    setEditingId(p.id); setShowForm(true);
  };
  const savePartner = async () => {
    if (!form.name.trim()) return toast.error("Le nom du partenaire est requis");
    setSaving(true);
    try {
      if (editingId) { await api.patch(`/admin/partners/${editingId}`, form); toast.success("Partenaire mis à jour"); }
      else { await api.post("/admin/partners", form); toast.success("Partenaire ajouté"); }
      setShowForm(false); setEditingId(null); setForm(emptyForm); loadPartners();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  const deletePartner = async (id) => {
    try { await api.delete(`/admin/partners/${id}`); setPartners((l) => l.filter((p) => p.id !== id)); toast.success("Partenaire supprimé"); }
    catch { toast.error("Suppression impossible"); }
  };
  const refLink = (code) => `${API}/r/${code}`;

  // ---- Brand kit (éditable) ----
  const [brand, setBrand] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const assetRef = useRef();
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/brand-kit"); setBrand(r.data); }
      catch { setBrand({ slogan: "", pitch: "", key_messages: [], links: [], assets: [] }); }
    })();
  }, []);
  const setB = (patch) => setBrand((b) => ({ ...b, ...patch }));
  const updMsg = (i, v) => setBrand((b) => ({ ...b, key_messages: b.key_messages.map((m, j) => (j === i ? v : m)) }));
  const addMsg = () => setBrand((b) => ({ ...b, key_messages: [...(b.key_messages || []), ""] }));
  const rmMsg = (i) => setBrand((b) => ({ ...b, key_messages: b.key_messages.filter((_, j) => j !== i) }));
  const updLink = (i, patch) => setBrand((b) => ({ ...b, links: b.links.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const addLink = () => setBrand((b) => ({ ...b, links: [...(b.links || []), { label: "", url: "" }] }));
  const rmLink = (i) => setBrand((b) => ({ ...b, links: b.links.filter((_, j) => j !== i) }));
  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      const r = await api.put("/admin/brand-kit", { slogan: brand.slogan, pitch: brand.pitch, key_messages: brand.key_messages, links: brand.links });
      setBrand(r.data); toast.success("Kit de marque enregistré");
    } catch { toast.error("Enregistrement impossible"); } finally { setSavingBrand(false); }
  };
  const uploadAsset = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image trop lourde (max 4 Mo)"); return; }
    setUploadingAsset(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try { const r = await api.post("/admin/brand-kit/asset", { label: file.name, data: reader.result }); setBrand((b) => ({ ...b, assets: [...(b.assets || []), r.data] })); toast.success("Visuel ajouté"); }
      catch { toast.error("Upload impossible"); } finally { setUploadingAsset(false); if (assetRef.current) assetRef.current.value = ""; }
    };
    reader.readAsDataURL(file);
  };
  const deleteAsset = async (id) => {
    try { await api.delete(`/admin/brand-kit/asset/${id}`); setBrand((b) => ({ ...b, assets: (b.assets || []).filter((a) => a.id !== id) })); toast.success("Visuel supprimé"); }
    catch { toast.error("Suppression impossible"); }
  };
  const assetUrl = (u) => `${BACKEND}${u}`;

  // ---- Connexions réseaux + IA ----
  const [conn, setConn] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [briefText, setBriefText] = useState("");
  const loadConn = async () => {
    try { const r = await api.get("/admin/social-connections"); setConn(r.data); }
    catch { setConn(null); }
  };
  useEffect(() => { loadConn(); }, []);
  const generateAI = async (setter) => {
    setGenLoading(true);
    try {
      const autoBrief = target === "general" ? "" : `hommage à ${selectedCandle?.deceased_name || ""}`;
      const brief = briefText.trim() || autoBrief;
      const r = await api.post("/admin/marketing/generate-post", { brief, tone: "chaleureux", network: (pform?.networks?.[0] || "facebook"), lang });
      setter(r.data.text); toast.success("Texte généré par l'IA");
    } catch { toast.error("Génération IA indisponible"); } finally { setGenLoading(false); }
  };
  const publishAuto = async (id) => {
    try { const r = await api.post(`/admin/social-posts/${id}/publish`); if (r.data.published) { toast.success("Publié automatiquement ✅"); loadPosts(); } else { toast.error("Échec — vérifiez les connexions réseaux"); } }
    catch { toast.error("Publication auto impossible"); }
  };

  // ---- Comptes de marque (multi-comptes) ----
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const account = accounts.find((a) => a.id === accountId) || null;
  const loadAccounts = async () => {
    try {
      const r = await api.get("/admin/social-accounts");
      setAccounts(r.data);
      setAccountId((cur) => cur && r.data.some((a) => a.id === cur) ? cur : (r.data[0]?.id || null));
    } catch { setAccounts([]); }
  };
  useEffect(() => { loadAccounts(); }, []);
  const addAccount = async () => {
    const name = window.prompt("Nom du nouveau compte (marque) ?");
    if (!name || !name.trim()) return;
    try { const r = await api.post("/admin/social-accounts", { name: name.trim() }); await loadAccounts(); setAccountId(r.data.id); toast.success(`Compte « ${r.data.name} » créé`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const renameAccount = async () => {
    if (!account) return;
    const name = window.prompt("Renommer le compte :", account.name);
    if (!name || !name.trim()) return;
    try { await api.patch(`/admin/social-accounts/${account.id}`, { name: name.trim() }); await loadAccounts(); toast.success("Compte renommé"); }
    catch { toast.error("Erreur"); }
  };
  const deleteAccount = async () => {
    if (!account) return;
    if (accounts.length <= 1) return toast.error("Gardez au moins un compte.");
    if (!window.confirm(`Supprimer le compte « ${account.name} » et TOUTES ses publications/connexions ?`)) return;
    try { await api.delete(`/admin/social-accounts/${account.id}`); const rest = accounts.filter((a) => a.id !== account.id); setAccounts(rest); setAccountId(rest[0]?.id || null); toast.success("Compte supprimé"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const setAutoSetting = async (patch) => {
    if (!account) return;
    try { const r = await api.patch(`/admin/social-accounts/${account.id}`, patch); setAccounts((l) => l.map((a) => a.id === account.id ? r.data : a)); }
    catch { toast.error("Erreur"); }
  };
  const toggleAutoNet = (k) => {
    if (!account) return;
    const cur = account.auto_networks || [];
    setAutoSetting({ auto_networks: cur.includes(k) ? cur.filter((n) => n !== k) : [...cur, k] });
  };

  // recharge les publications quand on change de compte
  useEffect(() => {
    if (!accountId) return;
    setLoadingPosts(true);
    api.get(`/admin/social-posts?account_id=${accountId}`).then((r) => setPosts(r.data)).catch(() => setPosts([])).finally(() => setLoadingPosts(false));
  }, [accountId]);

  // ---- Assistant IA multi-réseaux (Claude) ----
  const ASSIST_NETS = ["facebook", "instagram", "x", "linkedin", "tiktok", "youtube", "pinterest", "whatsapp", "telegram"];
  const [aTopic, setATopic] = useState("promo");
  const [aSource, setASource] = useState("");
  const [aLang, setALang] = useState("fr");
  const [aNets, setANets] = useState(["facebook", "instagram"]);
  const [aLink, setALink] = useState("");
  const [aBrief, setABrief] = useState("");
  const [aImage, setAImage] = useState(null);
  const [aBusy, setABusy] = useState(false);
  const [aResult, setAResult] = useState(null); // { network: {fr, en} }
  const assistImgRef = useRef();
  const toggleANet = (k) => setANets((v) => v.includes(k) ? v.filter((n) => n !== k) : [...v, k]);
  const onAssistImage = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image trop lourde (max 4 Mo)"); return; }
    const rd = new FileReader(); rd.onload = () => setAImage(rd.result); rd.readAsDataURL(file);
  };
  const runAssistant = async () => {
    if (aNets.length === 0) return toast.error("Choisissez au moins un réseau");
    setABusy(true); setAResult(null);
    try {
      const r = await api.post("/admin/social/assistant", {
        account_id: accountId, account_name: account?.name,
        topic_type: aTopic, source_id: aSource || null,
        networks: aNets, lang: aLang, link: aLink.trim() || null, brief: aBrief.trim() || null,
      });
      setAResult(r.data.generated || {});
      toast.success("Publications générées par l'IA ✨");
    } catch (e) { toast.error(e.response?.data?.detail || "Génération IA indisponible"); }
    finally { setABusy(false); }
  };
  const createFromAssistant = async (net, text) => {
    try {
      await api.post("/admin/social-posts", { account_id: accountId, networks: [net], text, image: aImage, link: aLink.trim() || null, status: "brouillon" });
      toast.success(`Brouillon créé pour ${net}`);
      if (accountId) { const r = await api.get(`/admin/social-posts?account_id=${accountId}`); setPosts(r.data); }
    } catch { toast.error("Erreur"); }
  };

  // ---- Analyse des retours ----
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [metricEdit, setMetricEdit] = useState(null); // { postId, net, values }
  const loadAnalytics = async () => {
    if (!accountId) return;
    setLoadingAnalytics(true);
    try { const r = await api.get(`/admin/social-analytics?account_id=${accountId}`); setAnalytics(r.data); }
    catch { setAnalytics(null); } finally { setLoadingAnalytics(false); }
  };
  useEffect(() => { if (sub === "analyse" && accountId) loadAnalytics(); }, [sub, accountId]);
  const saveMetrics = async (postId, existing) => {
    const net = metricEdit?.net;
    if (!net) return;
    const merged = { ...(existing || {}) };
    merged[net] = metricEdit.values;
    try { await api.patch(`/admin/social-posts/${postId}/metrics`, { metrics: merged }); toast.success("Statistiques enregistrées"); setMetricEdit(null); loadAnalytics(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid="marketing-externe">
      <div className="rounded-3xl border border-fuchsia-400/20 bg-fuchsia-500/[0.05] p-5">
        <div className="flex items-center gap-2 text-fuchsia-300"><Megaphone size={18} /><h2 className="text-lg font-semibold">Acquisition & communication externe</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">Faites connaître vos marques au-delà de vos clients : réseaux sociaux, partenaires et presse. Préparez vos publications, générez des liens traçables et suivez d'où viennent vos visiteurs.</p>
      </div>

      {/* Sélecteur de comptes (marques) */}
      <div className="rounded-3xl border border-white/10 bg-card/40 p-4 sm:p-5" data-testid="account-bar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Compte :</span>
          {accounts.map((a) => (
            <button key={a.id} type="button" onClick={() => setAccountId(a.id)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${a.id === accountId ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-100" : "border-white/15 bg-white/5 text-muted-foreground hover:bg-white/10"}`}
              data-testid={`account-tab-${a.id}`}>
              {a.logo && <img src={`${BACKEND}${a.logo}`} alt="" className="h-4 w-4 rounded-full object-cover" />}
              {a.name}
            </button>
          ))}
          <Button size="sm" variant="outline" className="rounded-full border-fuchsia-400/40 text-fuchsia-200" onClick={addAccount} data-testid="account-add-btn"><Plus size={14} className="mr-1" /> Ajouter un compte</Button>
          {account && (
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={renameAccount} data-testid="account-rename-btn"><Pencil size={14} /></Button>
              {accounts.length > 1 && <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={deleteAccount} data-testid="account-delete-btn"><Trash2 size={14} /></Button>}
            </div>
          )}
        </div>

        {account && (
          <div className="mt-4 rounded-2xl border border-sky-400/20 bg-sky-500/[0.04] p-4" data-testid="auto-reprogram">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-sky-200"><CalendarClock size={15} /> Reprogrammation automatique — {account.name}</div>
            <p className="mb-3 text-xs text-muted-foreground">Dès qu'une nouvelle flamme est allumée ou qu'un nouvel article de blog paraît, l'assistant prépare automatiquement une publication pour les réseaux ci-dessous.</p>
            <div className="flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-foreground/90">
                <input type="checkbox" checked={!!account.auto_new_flame} onChange={(e) => setAutoSetting({ auto_new_flame: e.target.checked })} data-testid="auto-flame-toggle" /> Nouvelles flammes
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-foreground/90">
                <input type="checkbox" checked={!!account.auto_new_blog} onChange={(e) => setAutoSetting({ auto_new_blog: e.target.checked })} data-testid="auto-blog-toggle" /> Nouveaux articles de blog
              </label>
              <div className="inline-flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Mode :</span>
                <Select value={account.auto_mode || "brouillon"} onValueChange={(v) => setAutoSetting({ auto_mode: v })}>
                  <SelectTrigger className="h-8 w-40" data-testid="auto-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brouillon">En brouillon</SelectItem>
                    <SelectItem value="programme">Programmer auto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <span className="text-xs text-muted-foreground">Réseaux ciblés :</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {CAMPAIGN_NETS.map((n) => (
                  <button key={n.key} type="button" onClick={() => toggleAutoNet(n.key)} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${(account.auto_networks || []).includes(n.key) ? "border-sky-400/60 bg-sky-500/15 text-sky-200" : "border-white/15 bg-white/5 text-muted-foreground hover:bg-white/10"}`} data-testid={`auto-net-${n.key}`}><n.icon size={12} /> {n.label}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Tabs value={sub} onValueChange={setSub} className="w-full">
        <DraggableSubTabs storageKey="bdc_mkt_externe_order_v2" testidPrefix="me-tab" items={[
          ["publications", "Publications & Campagnes"],
          ["assistant", "Assistant IA"],
          ["analyse", "Analyse"],
          ["connexions", "Connexions réseaux"],
          ["partenaires", "Partenaires"],
          ["ambassadeurs", "Ambassadeurs"],
          ["kit", "Kit de marque"],
          ["blog", "Blog SEO"],
        ]} />

        <TabsContent value="publications" className="mt-5 space-y-5">
      {/* 1. Générateur de publications */}
      <Section icon={Share2} title="Générateur de publications" desc="Préparez un post prêt à partager, puis publiez en un clic sur chaque réseau. Chaque lien est traçable (UTM).">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Sujet</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="post-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Le site (général)</SelectItem>
                {paidCandles.map((c) => (<SelectItem key={c.id} value={c.id}>Bougie · {c.deceased_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Langue du message</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="post-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Label className="flex items-center gap-1.5"><Megaphone size={13} className="text-fuchsia-300" /> Brief pour l'IA (optionnel)</Label>
          <Textarea rows={2} value={briefText} onChange={(e) => setBriefText(e.target.value)} placeholder="Guidez l'IA : offre, événement, date clé, ton, appel à l'action… Ex. « Toussaint : inviter à allumer une bougie gratuite, ton doux, mentionner le 1er novembre »." className="min-h-[56px] resize-y text-sm" data-testid="ai-brief" />
          <p className="mt-1 text-xs text-muted-foreground">Laissé vide, l'IA rédige à partir du sujet choisi ci-dessus.</p>
        </div>
        <div className="mt-3">
          <Label>Texte de la publication (modifiable)</Label>
          <Textarea rows={4} value={postText} onChange={(e) => setPostText(e.target.value)} className="min-h-[96px] resize-y text-sm" data-testid="post-text" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="post-share-buttons">
          {NETWORKS.map((n) => (
            <a key={n.key} href={shareUrl(n.key)} target="_blank" rel="noreferrer"
               className={`inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors ${n.cls}`}
               data-testid={`post-share-${n.key}`}>
              <n.icon size={14} /> {n.label}
            </a>
          ))}
          <button type="button" onClick={() => generateAI(setPostText)} disabled={genLoading} className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/10 px-3 py-1.5 text-xs font-medium text-fuchsia-200 transition-colors hover:bg-fuchsia-500/20" data-testid="ai-generate-generator">
            {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />} Générer avec l'IA
          </button>
          <button type="button" onClick={() => copy(postText, "Texte copié")} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-white/40 hover:text-white" data-testid="post-copy-text">
            <Copy size={14} /> Copier le texte
          </button>
          <button type="button" onClick={() => copy(linkFor("copy"), "Lien copié")} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-white/40 hover:text-white" data-testid="post-copy-link">
            <Link2 size={14} /> Copier le lien
          </button>
        </div>
      </Section>

      {/* 1b. Campagnes & programmation réseaux */}
      <Section icon={CalendarClock} title="Campagnes & programmation réseaux" desc="Planifiez vos publications sur les réseaux de BougieDuCœur. Programmez une date/heure : vous recevrez un rappel (cloche + e-mail) pour publier en un clic. L'auto-publication pourra être branchée quand vos comptes réseaux seront connectés.">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded-full border border-white/10 p-0.5" data-testid="posts-view-toggle">
            <button type="button" onClick={() => setViewMode("list")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === "list" ? "bg-white/10 text-white" : "text-muted-foreground hover:text-white"}`} data-testid="view-list"><List size={14} /> Liste</button>
            <button type="button" onClick={() => setViewMode("calendar")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === "calendar" ? "bg-white/10 text-white" : "text-muted-foreground hover:text-white"}`} data-testid="view-calendar"><CalendarDays size={14} /> Calendrier</button>
          </div>
          <Button size="sm" className="rounded-full" onClick={() => { setPform({ ...emptyPost, text: postText }); setShowPostForm(true); }} data-testid="post-plan-add"><Plus size={15} className="mr-1.5" /> Nouvelle campagne</Button>
        </div>

        {templates.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="templates-bar">
            <span className="text-xs text-muted-foreground">Mes modèles :</span>
            {templates.map((t) => (
              <span key={t.id} className="group inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 py-1 pl-3 pr-1 text-xs text-muted-foreground">
                <button type="button" onClick={() => { setPform({ ...emptyPost, networks: t.networks?.length ? t.networks : ["facebook"], text: t.text || "", image: t.image || null }); setShowPostForm(true); }} className="hover:text-fuchsia-200" data-testid={`template-use-${t.id}`}>{t.name}</button>
                <button type="button" onClick={() => deleteTemplate(t.id)} className="rounded-full p-0.5 text-muted-foreground/60 hover:text-destructive" title="Supprimer le modèle"><X size={12} /></button>
              </span>
            ))}
          </div>
        )}

        {showPostForm && (
          <div className="mb-5 space-y-3 rounded-2xl border border-fuchsia-400/25 bg-fuchsia-400/[0.04] p-4" data-testid="post-plan-form">
            <div>
              <Label>Réseaux</Label>
              <div className="flex flex-wrap gap-2">
                {CAMPAIGN_NETS.map((n) => (
                  <button key={n.key} type="button" onClick={() => toggleNet(n.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${pform.networks.includes(n.key) ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-200" : "border-white/15 bg-white/5 text-muted-foreground hover:bg-white/10"}`} data-testid={`post-net-${n.key}`}><n.icon size={14} /> {n.label}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="mb-0">Message</Label>
                <button type="button" onClick={() => setPform((f) => ({ ...f, text: postText }))} className="text-xs text-sky-300 hover:underline">Reprendre le texte du générateur</button>
              </div>
              <Textarea rows={3} value={pform.text} onChange={(e) => setPform({ ...pform, text: e.target.value })} className="min-h-[80px] resize-y text-sm" data-testid="post-plan-text" />
              <button type="button" onClick={() => generateAI((t) => setPform((f) => ({ ...f, text: t })))} disabled={genLoading} className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/10 px-3 py-1 text-xs font-medium text-fuchsia-200 transition-colors hover:bg-fuchsia-500/20" data-testid="ai-generate-campaign">{genLoading ? <Loader2 size={13} className="animate-spin" /> : <Megaphone size={13} />} Générer avec l'IA</button>
            </div>
            <div>
              <Label>Image (optionnelle)</Label>
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => postImgRef.current?.click()}><ImagePlus size={14} className="mr-1.5" /> {pform.image ? "Changer l'image" : "Ajouter une image"}</Button>
                <input ref={postImgRef} type="file" accept="image/*" className="hidden" onChange={onPostImage} />
                {pform.image && <img src={pform.image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                {pform.image && <button type="button" onClick={() => setPform({ ...pform, image: null })} className="text-xs text-destructive hover:underline">Retirer</button>}
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Eye size={13} className="text-fuchsia-300" /> Aperçu par réseau</Label>
              <p className="mb-2 text-xs text-muted-foreground">Rendu approximatif avant publication (l'affichage réel peut varier légèrement).</p>
              <div className="flex flex-wrap gap-4" data-testid="social-previews">
                {(pform.networks.includes("facebook") || pform.networks.length === 0 || (!pform.networks.includes("instagram"))) && (
                  <div>
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-[#4293ff]"><Facebook size={13} /> Facebook</div>
                    <SocialPreview network="facebook" text={pform.text} image={pform.image} />
                  </div>
                )}
                {(pform.networks.includes("instagram") || pform.networks.length === 0) && (
                  <div>
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-pink-400"><ImageIcon size={13} /> Instagram</div>
                    <SocialPreview network="instagram" text={pform.text} image={pform.image} />
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Quand publier ?</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={pform.mode} onValueChange={(v) => setPform({ ...pform, mode: v })}>
                  <SelectTrigger className="w-56" data-testid="post-plan-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="schedule">Programmer (date &amp; heure)</SelectItem>
                    <SelectItem value="draft">Enregistrer en brouillon</SelectItem>
                  </SelectContent>
                </Select>
                {pform.mode === "schedule" && (
                  <Input type="datetime-local" value={pform.scheduled_at} onChange={(e) => setPform({ ...pform, scheduled_at: e.target.value })} className="w-56" data-testid="post-plan-datetime" />
                )}
                {pform.mode === "schedule" && (
                  <Select value={pform.recurrence} onValueChange={(v) => setPform({ ...pform, recurrence: v })}>
                    <SelectTrigger className="w-44" data-testid="post-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Une seule fois</SelectItem>
                      <SelectItem value="daily">Tous les jours</SelectItem>
                      <SelectItem value="weekly">Toutes les semaines</SelectItem>
                      <SelectItem value="monthly">Tous les mois</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="rounded-full" onClick={savePost} disabled={savingPost} data-testid="post-plan-save">{savingPost ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Check size={15} className="mr-1.5" />} {pform.mode === "schedule" ? "Programmer" : "Enregistrer"}</Button>
              <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={saveAsTemplate} data-testid="template-save"><Bookmark size={15} className="mr-1.5" /> Enregistrer comme modèle</Button>
              <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => setShowPostForm(false)}><X size={15} className="mr-1.5" /> Annuler</Button>
            </div>
          </div>
        )}

        {viewMode === "calendar" ? (
          <div data-testid="posts-calendar">
            <div className="mb-3 flex items-center justify-between">
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
              <span className="text-sm font-medium capitalize">{monthLabel}</span>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight size={16} /></Button>
            </div>
            <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground">
              {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {buildCalendar().map((date, i) => date ? (
                <div key={i} className="min-h-[70px] rounded-lg border border-white/10 bg-black/20 p-1 text-left">
                  <div className="text-[11px] text-muted-foreground">{date.getDate()}</div>
                  {postsOnDay(date).map((p) => (
                    <div key={p.id} title={p.text} className="mt-0.5 truncate rounded bg-fuchsia-500/20 px-1 py-0.5 text-[10px] text-fuchsia-200" data-testid="cal-post">
                      {new Date(p.scheduled_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {(p.networks || [])[0] || ""}
                    </div>
                  ))}
                </div>
              ) : <div key={i} className="min-h-[70px] rounded-lg" />)}
            </div>
          </div>
        ) : loadingPosts ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Chargement…</p>
        ) : posts.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="posts-empty">Aucune campagne planifiée. Créez votre première publication.</p>
        ) : (
          <div className="space-y-2" data-testid="posts-list">
            {posts.map((p) => {
              const st = POST_STATUS[p.status] || POST_STATUS.brouillon;
              return (
                <div key={p.id} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="post-item">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                        {p.scheduled_at && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock size={12} /> {fmtDate(p.scheduled_at)}</span>}
                        {p.recurrence && p.recurrence !== "none" && <span className="inline-flex items-center gap-1 text-xs text-fuchsia-300"><CalendarClock size={12} /> {p.recurrence === "daily" ? "Quotidien" : p.recurrence === "weekly" ? "Hebdo" : "Mensuel"}</span>}
                        {(p.networks || []).map((n) => { const meta = CAMPAIGN_NETS.find((x) => x.key === n); const I = meta?.icon || Globe; return <span key={n} className="text-muted-foreground" title={meta?.label}><I size={13} /></span>; })}
                      </div>
                      <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground/90">{p.text}</p>
                    </div>
                    {p.image && <img src={p.image} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
                    <span className="text-xs text-muted-foreground">Publier :</span>
                    {(p.networks || []).map((n) => { const meta = CAMPAIGN_NETS.find((x) => x.key === n); const I = meta?.icon || Globe; return (
                      <button key={n} type="button" onClick={() => publishTo(n, p.text)} className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-fuchsia-400/50 hover:text-fuchsia-200" data-testid={`post-publish-${n}`}><I size={13} /> {meta?.label}</button>
                    ); })}
                    {p.image && <a href={p.image} download="visuel" className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:text-white"><Download size={13} /> Image</a>}
                    <div className="ml-auto flex items-center gap-1">
                      {(conn?.facebook_connected || conn?.instagram_connected) && p.status !== "publie" && <Button size="sm" variant="ghost" className="h-7 text-fuchsia-300" onClick={() => publishAuto(p.id)} data-testid="post-publish-auto"><Send size={14} className="mr-1" /> Publier auto</Button>}
                      {p.status !== "publie" && <Button size="sm" variant="ghost" className="h-7 text-emerald-300" onClick={() => markPublished(p.id)} data-testid="post-mark-published"><Check size={14} className="mr-1" /> Publié</Button>}
                      <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => deletePost(p.id)} data-testid="post-delete"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      </TabsContent>

        <TabsContent value="assistant" className="mt-5 space-y-5">
          <Section icon={Megaphone} title={`Assistant digital IA — ${account?.name || ""}`} desc="L'IA rédige des publications adaptées à chaque réseau (avec hashtags), en français et/ou en anglais, à partir d'un sujet. Ajoutez un visuel et un lien, puis créez un brouillon en un clic.">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Sujet</Label>
                <Select value={aTopic} onValueChange={(v) => { setATopic(v); setASource(""); }}>
                  <SelectTrigger data-testid="assist-topic"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="memorial">Nouveau mémorial / flamme</SelectItem>
                    <SelectItem value="blog">Article de blog</SelectItem>
                    <SelectItem value="awareness">Sensibilisation / hommage</SelectItem>
                    <SelectItem value="promo">Promotion du service</SelectItem>
                    <SelectItem value="custom">Libre (brief)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Langue</Label>
                <Select value={aLang} onValueChange={setALang}>
                  <SelectTrigger data-testid="assist-lang"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="both">Les deux (FR + EN)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {aTopic === "memorial" && (
                <div className="sm:col-span-2">
                  <Label>Flamme concernée</Label>
                  <Select value={aSource} onValueChange={setASource}>
                    <SelectTrigger data-testid="assist-source-candle"><SelectValue placeholder="Choisir une flamme…" /></SelectTrigger>
                    <SelectContent>{paidCandles.map((c) => (<SelectItem key={c.id} value={c.id}>{c.deceased_name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              )}
              {aTopic === "blog" && (
                <div className="sm:col-span-2">
                  <Label>Identifiant / slug de l'article</Label>
                  <Input value={aSource} onChange={(e) => setASource(e.target.value)} placeholder="ex. hommage-a-…" data-testid="assist-source-blog" />
                </div>
              )}
              <div className="sm:col-span-2">
                <Label>Lien cliquable (optionnel)</Label>
                <Input value={aLink} onChange={(e) => setALink(e.target.value)} placeholder="https://bougieducoeur.com/…" data-testid="assist-link" />
              </div>
              {aTopic === "custom" && (
                <div className="sm:col-span-2">
                  <Label>Brief</Label>
                  <Textarea rows={2} value={aBrief} onChange={(e) => setABrief(e.target.value)} placeholder="Décrivez le message souhaité…" data-testid="assist-brief" />
                </div>
              )}
            </div>
            <div className="mt-3">
              <Label>Réseaux</Label>
              <div className="flex flex-wrap gap-2">
                {CAMPAIGN_NETS.map((n) => (
                  <button key={n.key} type="button" onClick={() => toggleANet(n.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${aNets.includes(n.key) ? "border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-200" : "border-white/15 bg-white/5 text-muted-foreground hover:bg-white/10"}`} data-testid={`assist-net-${n.key}`}><n.icon size={14} /> {n.label}</button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <Label>Visuel (photo de flamme, blog, kit, ou import)</Label>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => assistImgRef.current?.click()}><ImagePlus size={14} className="mr-1.5" /> {aImage ? "Changer" : "Importer"}</Button>
                <input ref={assistImgRef} type="file" accept="image/*" className="hidden" onChange={onAssistImage} />
                {(brand?.assets || []).slice(0, 6).map((a) => (
                  <button key={a.id} type="button" onClick={() => setAImage(assetUrl(a.url))} title={a.label} className={`h-11 w-11 overflow-hidden rounded-lg border ${aImage === assetUrl(a.url) ? "border-fuchsia-400" : "border-white/15"}`}><img src={assetUrl(a.url)} alt="" className="h-full w-full object-cover" /></button>
                ))}
                {aImage && <img src={aImage} alt="" className="h-11 w-11 rounded-lg object-cover ring-2 ring-fuchsia-400" />}
                {aImage && <button type="button" onClick={() => setAImage(null)} className="text-xs text-destructive hover:underline">Retirer</button>}
              </div>
            </div>
            <Button className="mt-4 rounded-full" onClick={runAssistant} disabled={aBusy} data-testid="assist-generate-btn">{aBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Megaphone size={15} className="mr-2" />} Générer les publications</Button>

            {aResult && (
              <div className="mt-5 space-y-3" data-testid="assist-results">
                {aNets.filter((n) => aResult[n]).map((n) => {
                  const meta = CAMPAIGN_NETS.find((x) => x.key === n);
                  const I = meta?.icon || Globe;
                  return (
                    <div key={n} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid={`assist-card-${n}`}>
                      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-fuchsia-200"><I size={15} /> {meta?.label}</div>
                      {Object.entries(aResult[n]).map(([lg, text]) => (
                        <div key={lg} className="mb-2">
                          <div className="mb-1 text-[11px] uppercase text-muted-foreground">{lg === "fr" ? "Français" : "English"}</div>
                          <p className="whitespace-pre-wrap rounded-lg border border-white/10 bg-black/30 p-3 text-sm text-foreground/90">{text}</p>
                          <div className="mt-1.5 flex flex-wrap gap-2">
                            <button type="button" onClick={() => copy(text, "Texte copié")} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-muted-foreground hover:text-white" data-testid={`assist-copy-${n}-${lg}`}><Copy size={13} /> Copier</button>
                            <button type="button" onClick={() => createFromAssistant(n, text)} className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/10 px-3 py-1 text-xs text-fuchsia-200 hover:bg-fuchsia-500/20" data-testid={`assist-create-${n}-${lg}`}><Plus size={13} /> Créer un brouillon</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="analyse" className="mt-5 space-y-5">
          <Section icon={MousePointerClick} title={`Analyse des retours — ${account?.name || ""}`} desc="Suivez les performances de vos publications : clics, vues, partages, mentions J'aime. Saisissez les chiffres relevés sur chaque réseau (l'auto-import sera branché avec les connexions API).">
            {loadingAnalytics ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Chargement…</p>
            ) : !analytics ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Aucune donnée.</p>
            ) : (
              <div className="space-y-5" data-testid="analytics">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[["clicks", "Clics", MousePointerClick], ["views", "Vues", Eye], ["shares", "Partages", Share2], ["likes", "J'aime", Heart]].map(([k, label, Icon]) => (
                    <div key={k} className="rounded-2xl border border-white/10 bg-black/20 p-4 text-center" data-testid={`analytics-total-${k}`}>
                      <Icon size={16} className="mx-auto mb-1 text-fuchsia-300" />
                      <div className="text-2xl font-bold text-foreground">{analytics.totals[k]}</div>
                      <div className="text-[11px] text-muted-foreground">{label}</div>
                    </div>
                  ))}
                </div>
                {analytics.posts.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground" data-testid="analytics-empty">Aucune publication publiée. Publiez des posts pour suivre leurs retours ici.</p>
                ) : (
                  <div className="space-y-2" data-testid="analytics-posts">
                    {analytics.posts.map((p) => (
                      <div key={p.id} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="analytics-post">
                        <div className="flex items-start justify-between gap-3">
                          <p className="min-w-0 flex-1 truncate text-sm text-foreground/90">{p.text || "(sans texte)"}</p>
                          <div className="flex shrink-0 gap-2 text-xs text-muted-foreground">
                            <span title="Clics"><MousePointerClick size={12} className="inline" /> {p.totals.clicks}</span>
                            <span title="Vues"><Eye size={12} className="inline" /> {p.totals.views}</span>
                            <span title="Partages"><Share2 size={12} className="inline" /> {p.totals.shares}</span>
                            <span title="J'aime"><Heart size={12} className="inline" /> {p.totals.likes}</span>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(p.networks || []).map((n) => {
                            const meta = CAMPAIGN_NETS.find((x) => x.key === n); const I = meta?.icon || Globe;
                            return (
                              <button key={n} type="button" onClick={() => setMetricEdit({ postId: p.id, net: n, values: { ...{ clicks: 0, views: 0, shares: 0, likes: 0 }, ...(p.metrics?.[n] || {}) } })} className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-muted-foreground hover:border-fuchsia-400/50 hover:text-fuchsia-200" data-testid={`analytics-edit-${p.id}-${n}`}><I size={12} /> {meta?.label} <Pencil size={11} /></button>
                            );
                          })}
                        </div>
                        {metricEdit?.postId === p.id && (
                          <div className="mt-2 rounded-xl border border-fuchsia-400/25 bg-fuchsia-400/[0.04] p-3" data-testid="analytics-metric-form">
                            <div className="mb-2 text-xs font-medium text-fuchsia-200">Statistiques — {CAMPAIGN_NETS.find((x) => x.key === metricEdit.net)?.label}</div>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                              {["clicks", "views", "shares", "likes"].map((k) => (
                                <div key={k}><Label className="text-[11px] capitalize">{k}</Label><Input type="number" value={metricEdit.values[k]} onChange={(e) => setMetricEdit((m) => ({ ...m, values: { ...m.values, [k]: e.target.value } }))} data-testid={`metric-${k}`} /></div>
                              ))}
                            </div>
                            <div className="mt-2 flex gap-2">
                              <Button size="sm" className="rounded-full" onClick={() => saveMetrics(p.id, p.metrics)} data-testid="metric-save"><Check size={14} className="mr-1" /> Enregistrer</Button>
                              <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => setMetricEdit(null)}><X size={14} className="mr-1" /> Annuler</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="connexions" className="mt-5">
          <SocialConnectionsPanel accountId={accountId} />
        </TabsContent>

        <TabsContent value="partenaires" className="mt-5 space-y-5">
      {/* 3. Réseaux partenaires (CRM) */}
      <Section icon={Handshake} title="Réseaux partenaires" desc="Gérez vos partenaires (pompes funèbres, fleuristes, presse…). Chacun reçoit un lien de parrainage traçable — vous voyez le nombre de visiteurs qu'il vous amène." accent="text-emerald-300">
        <div className="mb-4 flex justify-end">
          <Button size="sm" className="rounded-full" onClick={openAdd} data-testid="partner-add-btn"><Plus size={15} className="mr-1.5" /> Ajouter un partenaire</Button>
        </div>

        {showForm && (
          <div className="mb-4 space-y-3 rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.04] p-4" data-testid="partner-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Pompes funèbres Martin" data-testid="partner-name" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="partner-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Contact</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Nom du contact" data-testid="partner-contact" /></div>
              <div><Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="partner-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PARTNER_STATUS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@partenaire.fr" data-testid="partner-email" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="partner-phone" /></div>
              <div className="sm:col-span-2"><Label>Site web</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="partner-website" /></div>
              <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="partner-notes" /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full" onClick={savePartner} disabled={saving} data-testid="partner-save-btn">{saving ? "…" : <><Check size={15} className="mr-1.5" /> Enregistrer</>}</Button>
              <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="partner-cancel-btn"><X size={15} className="mr-1.5" /> Annuler</Button>
            </div>
          </div>
        )}

        {loadingP ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Chargement…</p>
        ) : partners.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="partners-empty">Aucun partenaire pour l'instant. Ajoutez vos premiers relais d'acquisition.</p>
        ) : (
          <div className="space-y-2" data-testid="partners-list">
            {partners.map((p) => {
              const st = statusMeta(p.status);
              return (
                <div key={p.id} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="partner-item">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-foreground">{p.name}</span>
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-muted-foreground">{typeLabel(p.type)}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {p.contact_name && <span>{p.contact_name}</span>}
                        {p.email && <span>{p.email}</span>}
                        {p.phone && <span>{p.phone}</span>}
                        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">{p.website}</a>}
                      </div>
                      {p.notes && <p className="mt-1 text-xs text-foreground/70">{p.notes}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span className="mr-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300" title="Visiteurs amenés" data-testid="partner-clicks"><MousePointerClick size={13} /> {p.clicks}</span>
                      <Button size="sm" variant="ghost" className="h-8 text-foreground/70" onClick={() => openEdit(p)} data-testid="partner-edit-btn"><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => deletePartner(p.id)} data-testid="partner-delete-btn"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5">
                    <Link2 size={13} className="shrink-0 text-emerald-300" />
                    <code className="min-w-0 flex-1 truncate text-[11px] text-emerald-200" data-testid="partner-ref-link">{refLink(p.referral_code)}</code>
                    <button type="button" onClick={() => copy(refLink(p.referral_code), "Lien de parrainage copié")} className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-white/10 hover:text-white" title="Copier le lien de parrainage" data-testid="partner-copy-ref"><Copy size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      </TabsContent>

        <TabsContent value="ambassadeurs" className="mt-5">
          <AmbassadorPanel />
        </TabsContent>

        <TabsContent value="kit" className="mt-5 space-y-5">
      {/* 4. Kit média & marque (éditable) */}
      <Section icon={ImageIcon} title="Kit média & marque (éditable)" desc="Modifiez librement toute l'identité marketing de la marque. Cliquez sur Enregistrer pour conserver vos changements." accent="text-amber-300">
        {!brand ? (
          <p className="py-4 text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <div className="space-y-5" data-testid="brand-kit-editor">
            <div>
              <Label>Slogan / accroche</Label>
              <div className="flex gap-2">
                <Input value={brand.slogan || ""} onChange={(e) => setB({ slogan: e.target.value })} placeholder="Une phrase forte qui résume la marque" data-testid="brand-slogan" />
                <Button size="icon" variant="outline" className="shrink-0 rounded-full border-white/15" onClick={() => copy(brand.slogan || "", "Slogan copié")} title="Copier" data-testid="brand-slogan-copy"><Copy size={14} /></Button>
              </div>
            </div>

            <div>
              <Label>Pitch presse / description</Label>
              <Textarea rows={3} value={brand.pitch || ""} onChange={(e) => setB({ pitch: e.target.value })} className="min-h-[84px] resize-y text-sm" placeholder="Le texte de présentation envoyé aux médias et partenaires" data-testid="brand-pitch" />
              <button type="button" onClick={() => copy(brand.pitch || "", "Pitch copié")} className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-white/40 hover:text-white" data-testid="brand-pitch-copy"><Copy size={13} /> Copier le pitch</button>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label className="mb-0">Messages clés</Label>
                <Button size="sm" variant="outline" className="h-7 rounded-full border-white/15" onClick={addMsg} data-testid="brand-add-message"><Plus size={13} className="mr-1" /> Ajouter</Button>
              </div>
              <div className="space-y-2" data-testid="brand-messages">
                {(brand.key_messages || []).length === 0 && <p className="text-xs text-muted-foreground">Aucun message clé.</p>}
                {(brand.key_messages || []).map((m, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={m} onChange={(e) => updMsg(i, e.target.value)} placeholder="Argument marketing" data-testid={`brand-message-${i}`} />
                    <Button size="icon" variant="ghost" className="shrink-0 text-destructive" onClick={() => rmMsg(i)} data-testid={`brand-message-remove-${i}`}><Trash2 size={14} /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label className="mb-0">Ressources média (liens)</Label>
                <Button size="sm" variant="outline" className="h-7 rounded-full border-white/15" onClick={addLink} data-testid="brand-add-link"><Plus size={13} className="mr-1" /> Ajouter un lien</Button>
              </div>
              <div className="space-y-2" data-testid="brand-links">
                {(brand.links || []).length === 0 && <p className="text-xs text-muted-foreground">Aucun lien. Ajoutez logo, vidéo, dossier de presse…</p>}
                {(brand.links || []).map((l, i) => (
                  <div key={l.id || i} className="flex flex-wrap items-center gap-2">
                    <Input value={l.label} onChange={(e) => updLink(i, { label: e.target.value })} placeholder="Nom (ex. Logo)" className="w-40 shrink-0" data-testid={`brand-link-label-${i}`} />
                    <Input value={l.url} onChange={(e) => updLink(i, { url: e.target.value })} placeholder="https://…" className="min-w-0 flex-1" data-testid={`brand-link-url-${i}`} />
                    {l.url && <a href={l.url} target="_blank" rel="noreferrer" className="shrink-0 rounded-full p-2 text-muted-foreground hover:bg-white/10 hover:text-white" title="Ouvrir"><Download size={14} /></a>}
                    {l.url && <button type="button" onClick={() => copy(l.url, "Lien copié")} className="shrink-0 rounded-full p-2 text-muted-foreground hover:bg-white/10 hover:text-white" title="Copier"><Copy size={14} /></button>}
                    <Button size="icon" variant="ghost" className="shrink-0 text-destructive" onClick={() => rmLink(i)} data-testid={`brand-link-remove-${i}`}><Trash2 size={14} /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <Label className="mb-0">Visuels (images hébergées)</Label>
                <Button size="sm" variant="outline" className="h-7 rounded-full border-white/15" onClick={() => assetRef.current?.click()} disabled={uploadingAsset} data-testid="brand-upload-asset">
                  {uploadingAsset ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />} Ajouter une image
                </Button>
                <input ref={assetRef} type="file" accept="image/*" className="hidden" onChange={uploadAsset} />
              </div>
              {(brand.assets || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun visuel. Importez votre logo, vos bannières…</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4" data-testid="brand-assets">
                  {(brand.assets || []).map((a) => (
                    <div key={a.id} className="group relative overflow-hidden rounded-xl border border-white/10 bg-black/20" data-testid="brand-asset-item">
                      <img src={assetUrl(a.url)} alt={a.label} className="h-28 w-full bg-white/5 object-contain p-2" />
                      <div className="flex items-center justify-between gap-1 border-t border-white/10 px-2 py-1.5">
                        <span className="min-w-0 truncate text-[11px] text-muted-foreground" title={a.label}>{a.label}</span>
                        <div className="flex shrink-0">
                          <button type="button" onClick={() => copy(assetUrl(a.url), "Lien image copié")} className="rounded-full p-1 text-muted-foreground hover:bg-white/10 hover:text-white" title="Copier le lien"><Copy size={13} /></button>
                          <button type="button" onClick={() => deleteAsset(a.id)} className="rounded-full p-1 text-destructive hover:bg-destructive/15" title="Supprimer" data-testid={`brand-asset-delete-${a.id}`}><Trash2 size={13} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={saveBrand} disabled={savingBrand} className="w-full rounded-full sm:w-auto" data-testid="brand-save-btn">
              {savingBrand ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={15} className="mr-2" />} Enregistrer le kit de marque
            </Button>
          </div>
        )}
      </Section>
        </TabsContent>

        <TabsContent value="blog" className="mt-5">
          <BlogManager candles={candles} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default MarketingExterne;
